import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import PersonAvatar from '../../components/common/PersonAvatar';

import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';
import './EventDoor.css';

/* The door — who is here, marked by hand.

   The host screen's counts said how many and never who. This is the screen you hold in front of
   you while people walk in: a face, a name, and one button per person. Three things happen here
   and nothing else.

     **Is here** — the scan did not work, or there is no scan to do (QR check-in is off on most
     events, and then this screen is the only mechanism there is). Same bit as the scanner writes,
     so the counts, the breakouts and the engagement score all move with it.
     **Undo** — a mistap at a door is a certainty, so taking it back is a button and not a support
     ticket.
     **Add someone who showed up** — somebody on no list at all, let in by the host.

   ★ Behind `_host_gate` on the server, not the admin gate. That is the difference between this
   screen and /company-event/:id/people: on a public Lab there is no organising company, so nobody
   is the "engagement admin" and that screen 404s at the one person actually running the evening.

   ★ Two lists, because a private company event asks a different question at the door than a Lab
   does. `expected` is everyone taking part. `others` is everyone who COULD have come and has not
   registered — the company roster, or the guest list on an invite-only event — and marking one of
   them here registers them in the same tap. On a Lab there is no such set (anybody may buy a
   seat), so `has_roster` is false and the second list is absent rather than drawn empty. */

const EventDoor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [params] = useSearchParams();

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);      // the partner id being written, or 'walk-in'
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState('');
  // ★ Read once at mount. `usePersistUrlParams` strips query params a tick after they appear, so
  // reading ?add= on every render would flip the form shut again — the same reason the host
  // screen keeps `present` in state.
  const [adding, setAdding] = useState(() => params.get('add') === '1');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  // The roster list is long and the coming list is the point, so it opens closed — but the search
  // box below reaches into it either way, because at a door you type a name, you do not scroll.
  const [showOthers, setShowOthers] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/door`)
      .then(setData)
      // Not the host, not the admin, or not an event that exists — none of which this screen can
      // do anything about. Back to the event itself, exactly as the host screen does.
      .catch(() => navigate(`/public-event/${eventId}`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const event = (data && data.event) || {};
  const counts = (data && data.counts) || {};
  const expected = useMemo(() => (data && data.expected) || [], [data]);
  const others = useMemo(() => (data && data.others) || [], [data]);

  /* Still-expected first, then name order. The queue is the point of this screen: the people who
     have not arrived are the ones you are looking for, and once somebody is in they only need to
     stay findable. Name order inside each half comes from the server. */
  const ordered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (p) => !needle
      || (p.name || '').toLowerCase().includes(needle)
      || (p.email || '').toLowerCase().includes(needle);
    return expected.filter(match).sort((a, b) => (a.attended === b.attended ? 0
      : (a.attended ? 1 : -1)));
  }, [expected, q]);

  const otherRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return others;
    return others.filter((p) => (p.name || '').toLowerCase().includes(needle)
      || (p.email || '').toLowerCase().includes(needle));
  }, [others, q]);

  // A search that finds somebody in the roster must SHOW them, whether or not the section is open
  // — otherwise typing a name that is right there returns "nobody".
  const othersOpen = showOthers || !!q.trim();

  const mark = (person, attended) => {
    if (busy) return;
    setBusy(person.id);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/door/attend`,
               { partner_id: person.id, attended })
      .then((res) => {
        setNotice(attended
          ? t('markedHere', { name: person.name || person.email })
          : t('markedNotHere', { name: person.name || person.email }));
        setData((d) => (d ? { ...d, counts: res.counts || d.counts } : d));
        load();
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(null));
  };

  const addWalkIn = (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy('walk-in');
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/door/walk-in`, form)
      .then((res) => {
        const person = res.person || {};
        setNotice(t('markedHere', { name: person.name || form.email }));
        setForm({ first_name: '', last_name: '', email: '' });
        setAdding(false);
        load();
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(null));
  };

  const row = (p) => (
    <div key={p.id} className={`ce-person ce-person--audience ${p.attended ? 'ed-row--here' : ''}`}>
      <PersonAvatar person={p} />
      <span className="ce-person__body">
        <span className="ce-person__name">{p.name || p.email}</span>
        <span className="ce-person__email">{p.job_title || p.email}</span>
      </span>
      <span className="ce-person__acts">
        {p.attended ? (
          <>
            <span className="ed-pill"><Icon name="check" /> {t('hereAlready')}</span>
            <button
              type="button"
              className="ce-person__act ce-person__act--send"
              disabled={!!busy}
              onClick={() => mark(p, false)}
            >
              {t('undoHere')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="ce-person__act"
            disabled={!!busy}
            onClick={() => mark(p, true)}
          >
            <Icon name="user-check" /> {t('markHere')}
          </button>
        )}
      </span>
    </div>
  );

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to={`/host-event/${eventId}`} />
          <Title my="1" className="mt-3">{t('loading')}</Title>
          <div className="hv-skel hv-skel--line" />
          <div className="hv-skel hv-skel--line hv-skel--short" />
        </Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/host-event/${eventId}`} />
        <Title my="1" className="mt-3">
          <Icon name="user-check" theme="primary" className="me-2" />
          {t('whosHere')}
        </Title>
        <p className="bravos-q__hint mb-2">{event.name}</p>

        <div className="hv-counts ed-counts">
          {[['attended', counts.attended], ['registered', counts.registered]].map(([key, n]) => (
            <div key={key} className="hv-count">
              <span className="hv-count__n">{n != null ? n : '–'}</span>
              <span className="hv-count__label">{t(`segment_${key}`)}</span>
            </div>
          ))}
        </div>

        {/* Why nobody is scanning. Without it an empty "here" column under a scanner button on
            the previous screen reads as something being broken. */}
        {!event.qr_attendance && <p className="ce-note">{t('qrCheckInOff')}</p>}

        <input
          type="search"
          className="ed-input mt-2"
          placeholder={t('searchPeople')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="bravos-form__row mt-2">
          <button type="button" className="bravos-copy" onClick={() => setAdding((v) => !v)}>
            <Icon name="plus" /> {t('addWalkIn')}
          </button>
        </div>

        {adding && (
          <form className="bravos-form mt-2" onSubmit={addWalkIn}>
            {/* ★ The address is not paperwork. It is how the platform tells one person from
                another, so with it somebody who already has an account is recognised instead of
                duplicated — and it is where the welcome mail goes. */}
            <p className="bravos-q__hint mb-2">{t('addWalkInNote')}</p>
            <input
              className="ed-input"
              placeholder={t('walkInFirstName')}
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            />
            <input
              className="ed-input"
              placeholder={t('walkInLastName')}
              value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            />
            <input
              className="ed-input"
              type="email"
              required
              placeholder={t('walkInEmail')}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <button type="submit" className="bravos-submit" disabled={busy === 'walk-in'}>
              {t('addAndMarkHere')}
            </button>
          </form>
        )}

        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      <Card>
        {ordered.length === 0 ? (
          <p className="ce-note">{t('nobodyHere')}</p>
        ) : ordered.map(row)}
      </Card>

      {/* Everyone who could have come and has not registered: the company roster, or the guest
          list on an invite-only event. Absent entirely on a Lab — there is no such list when
          anybody may buy a seat, and an empty section reads as "nobody was invited". */}
      {data.has_roster && others.length > 0 && (
        <Card>
          <button
            type="button"
            className="ed-fold"
            onClick={() => setShowOthers((v) => !v)}
          >
            <Icon name={othersOpen ? 'angle-up' : 'angle-down'} />
            {' '}
            {t(data.is_invite_only ? 'invitedNotRegistered' : 'notRegisteredYet')}
            <span className="ce-tab__n">{others.length}</span>
          </button>
          {othersOpen && (
            <>
              <p className="ce-note">{t('markHereRegistersThem')}</p>
              {otherRows.length === 0
                ? <p className="ce-note">{t('nobodyHere')}</p>
                : otherRows.map(row)}
            </>
          )}
        </Card>
      )}
    </Base>
  );
};

export default EventDoor;
